import { useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useRef } from 'react'
import {
	AvailableExportTarget,
	availableExportTargetsAtom,
	exportConfigAtom,
	ExportStatus,
	exportStatusAtom,
	exportTargetNextCursorAtom,
	isLoadingAvailableTargetsAtom,
	isLoadingExportAtom,
	isLoadingMoreTargetsAtom,
	isResolvingExportTargetAtom,
	loadMoreExportTargetsAtom,
	selectedExportTargetAtom,
} from '../state'

export type UseExportOptionsArguments = {
	exportStatus: ExportStatus
	availableExportTargets: AvailableExportTarget[]
	isLoadingExport: boolean
	isLoadingAvailableTargets: boolean
	nextCursor: string | null
	loadMore: (() => void) | null
	isLoadingMore: boolean
}

/**
 * UI hook that syncs export display options to atoms.
 * This is called by the route hook, keeping UI concerns in the UI layer.
 */
export function useExportOptions({
	exportStatus,
	availableExportTargets,
	isLoadingExport,
	isLoadingAvailableTargets,
	nextCursor,
	loadMore,
	isLoadingMore,
}: UseExportOptionsArguments) {
	const setExportStatus = useSetAtom(exportStatusAtom)
	const setAvailableExportTargets = useSetAtom(availableExportTargetsAtom)
	const setSelectedExportTarget = useSetAtom(selectedExportTargetAtom)
	const exportConfig = useAtomValue(exportConfigAtom)
	const setIsLoadingExport = useSetAtom(isLoadingExportAtom)
	const setIsLoadingAvailableTargets = useSetAtom(isLoadingAvailableTargetsAtom)
	const setNextCursor = useSetAtom(exportTargetNextCursorAtom)
	const setLoadMore = useSetAtom(loadMoreExportTargetsAtom)
	const setIsLoadingMore = useSetAtom(isLoadingMoreTargetsAtom)
	const setIsResolvingExportTarget = useSetAtom(isResolvingExportTargetAtom)

	useEffect(() => {
		setExportStatus(exportStatus)
	}, [exportStatus, setExportStatus])

	useEffect(() => {
		setAvailableExportTargets(availableExportTargets)
	}, [availableExportTargets, setAvailableExportTargets])

	const lastSyncedTargetId = useRef<string | null>(null)
	const isExportConfigLoaded = exportConfig !== undefined
	const exportTargetId = exportConfig?.exportTargetId ?? null

	// Resolve the selected export target whenever the config or available targets change.
	// Skips redundant updates when the target hasn't changed (via lastSyncedTargetId ref).
	useEffect(() => {
		if (!isExportConfigLoaded) {
			setIsResolvingExportTarget(true)
			return
		}

		const alreadySynced = lastSyncedTargetId.current === exportTargetId

		if (alreadySynced) {
			setIsResolvingExportTarget(false)
			return
		}

		const resolvedTarget = exportTargetId
			? availableExportTargets.find((target) => target.id === exportTargetId) ?? null
			: null

		const isFetchingSelectedTarget = exportTargetId && !resolvedTarget && isLoadingAvailableTargets

		if (isFetchingSelectedTarget) {
			setIsResolvingExportTarget(true)
			return
		}

		setSelectedExportTarget(resolvedTarget)
		lastSyncedTargetId.current = resolvedTarget?.id ?? null

		setIsResolvingExportTarget(false)
	}, [
		isExportConfigLoaded,
		exportTargetId,
		availableExportTargets,
		isLoadingAvailableTargets,
		setSelectedExportTarget,
		setIsResolvingExportTarget,
	])

	useEffect(() => {
		setIsLoadingExport(isLoadingExport)
	}, [isLoadingExport, setIsLoadingExport])

	useEffect(() => {
		setIsLoadingAvailableTargets(isLoadingAvailableTargets)
	}, [isLoadingAvailableTargets, setIsLoadingAvailableTargets])

	useEffect(() => {
		setNextCursor(nextCursor)
	}, [nextCursor, setNextCursor])

	useEffect(() => {
		setLoadMore(() => loadMore)
	}, [loadMore, setLoadMore])

	useEffect(() => {
		setIsLoadingMore(isLoadingMore)
	}, [isLoadingMore, setIsLoadingMore])
}
